'use client';

// Development-only planner fixture (`?auditFixture=degree-planner-views`). It renders the real
// PlannerWorkspace over a synthetic student with in-memory edits so every view can be reviewed
// and screenshotted without an account. Nothing here is saved or represents a real record.
// It also exercises the review flows: "Auto-fill the rest" runs the real engine and shows its
// proposal as ghost cards, and the example saved lists back "Your lists" in Add and slot flows.

import React, { useCallback, useMemo, useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import type { Semester, SemesterItem } from '@/app/types/interfaces';
import { normalize } from '@/app/services/degreeRequirementService';
import { useCatalogCourses } from '@/app/services/useCatalogCourses';
import { generateAutoFillPlan } from '@/app/services/degreeAutoFillEngine';
import PlannerWorkspace from './PlannerWorkspace';
import { applyProposal, type ProposedTermInput } from './planProposal';
import { buildCatalogIndex, buildPlacement, buildTerms, formatCode, prerequisiteClauses } from './plannerModel';
import type { PlanIssue, UserCourseList } from './plannerContext';
import DegreeTargets from '../components/DegreeTargets';
import { programColor } from './plannerModel';

const course = (code: string, semester: number, credits = 4): SemesterItem => ({ kind: 'course', code, name: '', credits, semester, completed: false });

const INITIAL: Semester[] = [
  { number: 1, name: 'Fall 2026', totalCredits: 0, courses: [course('01:198:206', 1), course('01:198:211', 1), course('01:640:250', 1, 3), course('01:355:101', 1, 3)] },
  { number: 2, name: 'Spring 2027', totalCredits: 0, courses: [course('01:198:344', 2), course('01:198:336', 2), course('01:640:251', 2)] },
  { number: 3, name: 'Fall 2027', totalCredits: 0, courses: [course('01:198:416', 3), { kind: 'course_list', mappingName: 'fixture_cs_upper', courseListId: 0, label: 'Upper-level CS elective', credits: 4, semester: 3, completed: false }] },
  { number: 4, name: 'Spring 2028', totalCredits: 0, courses: [] },
];

const COMPLETED = ['01:198:111', '01:198:112', '01:198:205', '01:640:151', '01:640:152', '01:355:100'].map((code) => ({ course_code: code }));

const LISTS = new Map<string, { classes: string[]; name?: string }>([
  ['fixture_cs_upper', { name: 'Upper-level CS elective', classes: ['01:198:314', '01:198:323', '01:198:415', '01:198:440', '01:198:462', '01:198:431'] }],
  ['fixture_math_upper', { name: 'Math elective (300+)', classes: ['01:640:300', '01:640:311', '01:640:350', '01:640:477'] }],
]);

// Example saved course lists, standing in for courseListsApi.get_all_user_course_lists.
const USER_LISTS: UserCourseList[] = [
  { id: 'fixture-1', name: 'CS electives I like', classes: ['01:198:314', '01:198:323', '01:198:440', '01:198:462'].map(normalize) },
  { id: 'fixture-2', name: 'Science options', classes: ['01:750:203', '01:750:204', '01:160:161', '01:160:162'].map(normalize) },
  { id: 'fixture-3', name: 'Gen eds to try', classes: ['01:730:101', '01:920:101', '01:830:101', '01:070:101', '01:450:101', '01:512:103', '01:510:101'].map(normalize) },
];

const REQUIREMENTS = [
  {
    degreeId: 731,
    degreeName: 'Computer Science B.S. (example)',
    requirements: [
      { id: 'cs-core', title: 'Computer science core', logic: 'and', requirements: [
        { id: 'cs-intro', title: 'Introductory sequence', description: '01:198:111 and 01:198:112', courses_needed: 2, course_pool: ['01:198:111', '01:198:112'] },
        { id: 'cs-disc', title: 'Discrete structures', description: '01:198:205 and 01:198:206', courses_needed: 2, course_pool: ['01:198:205', '01:198:206'] },
        { id: 'cs-sys', title: 'Systems and algorithms', description: '01:198:211, 214 and 344', courses_needed: 3, course_pool: ['01:198:211', '01:198:214', '01:198:344'] },
      ] },
      { id: 'cs-electives', title: 'Electives', logic: 'and', requirements: [
        { id: 'cs-upper', title: 'Upper-level CS electives', description: 'Two courses from the 300+ CS elective list', courses_needed: 2, course_pool: ['fixture_cs_upper'] },
        { id: 'cs-more', title: 'Additional CS electives', description: 'Three more CS electives', courses_needed: 3, course_pool: ['01:198:336', '01:198:416', 'fixture_cs_upper'] },
      ] },
      { id: 'cs-math', title: 'Mathematics and science', logic: 'and', requirements: [
        { id: 'cs-calc', title: 'Math foundation', description: '01:640:151, 152 and 250', courses_needed: 3, course_pool: ['01:640:151', '01:640:152', '01:640:250'] },
        { id: 'cs-sci', title: 'Science sequence', description: 'Physics 203–204 or Chemistry 161–162', courses_needed: 2, course_pool: ['01:750:203', '01:750:204', '01:160:161', '01:160:162'] },
      ] },
    ],
  },
  {
    degreeId: 9321,
    degreeName: 'Mathematics minor (example)',
    requirements: [
      { id: 'm-core', title: 'Minor requirements', logic: 'and', requirements: [
        { id: 'm-calc', title: 'Calculus', description: '01:640:151, 152 and 251', courses_needed: 3, course_pool: ['01:640:151', '01:640:152', '01:640:251'] },
        { id: 'm-lin', title: 'Linear algebra', description: '01:640:250', courses_needed: 1, course_pool: ['01:640:250'] },
        { id: 'm-upper', title: 'Math electives (300+)', description: 'Two math courses numbered 300 or above', courses_needed: 2, course_pool: ['fixture_math_upper'] },
      ] },
    ],
  },
];

export default function PlannerFixture({ transfer = false }: { transfer?: boolean }) {
  const catalog = useCatalogCourses();
  const index = useMemo(() => buildCatalogIndex(catalog), [catalog]);
  const [semesters, setSemesters] = useState<Semester[]>(INITIAL);
  const [proposedTerms, setProposedTerms] = useState<ProposedTermInput[] | null>(null);
  const [autoFillNote, setAutoFillNote] = useState<string | null>(null);
  const creditsMap = useMemo(() => {
    const map = new Map<string, number>();
    catalog.forEach((c) => { const credits = Number(c.credits); if (credits > 0) map.set(normalize(c.baseId), credits); });
    return map;
  }, [catalog]);
  const expandedPools = useMemo(() => new Map(Array.from(LISTS.entries()).map(([name, list]) => [name, list.classes])), []);
  const completed = useMemo(() => new Set(COMPLETED.map((c) => normalize(c.course_code))), []);

  const issues = useMemo<PlanIssue[]>(() => {
    const terms = buildTerms(semesters, creditsMap, () => 4);
    const placement = buildPlacement(terms);
    const out: PlanIssue[] = [];
    placement.forEach((where, code) => {
      const missing = prerequisiteClauses(code, where.order, index, completed, placement).filter((c) => c.status === 'missing' || c.status === 'late');
      if (missing.length) out.push({ type: 'prerequisite', course: formatCode(code), issue: `Missing prerequisites: ${missing.map((c) => c.options.map(formatCode).join(' or ')).join(', ')}`, suggestion: 'Move to a later semester or add prerequisites to earlier semesters' });
    });
    return out;
  }, [semesters, creditsMap, index, completed]);

  const update = useCallback((fn: (draft: Semester[]) => void) => {
    setSemesters((current) => { const draft = JSON.parse(JSON.stringify(current)) as Semester[]; fn(draft); return draft; });
  }, []);

  const runAutoFill = () => {
    const courseNameMap = new Map(catalog.map((c) => [normalize(c.baseId), c.courseName] as const));
    const result = generateAutoFillPlan(REQUIREMENTS.flatMap((r) => r.requirements), {
      maxCreditsPerSemester: 18,
      minCreditsPerSemester: 12,
      startSeason: 'Fall',
      startYear: 2026,
      completedCourses: Array.from(completed),
      expandedPools,
      creditsMap,
      courseNameMap,
      degreeCourseListsDirect: LISTS,
      catalogCourses: catalog,
    });
    const proposed = result.semesters.map((term) => ({ name: term.name, courses: term.courses }));
    setAutoFillNote(proposed.length ? null : 'Auto-fill found nothing to suggest for this example.');
    setProposedTerms(proposed.length ? proposed : null);
  };
  const discardProposal = useCallback(() => setProposedTerms(null), []);
  const plannedCredits = semesters.reduce((sum, term) => sum + term.courses.reduce((total, item) => total + (Number(item.credits) || 0), 0), 0);

  return (
    <div className="mx-auto max-w-[1860px] px-4 py-6 sm:px-6 2xl:px-10" data-audit-ready="degree-planner-views">
      <p className="mb-3 inline-block rounded-lg bg-[var(--ss-warning-bg)] px-2.5 py-1 text-xs font-semibold text-[var(--ss-warning)]">Example student for design review. Nothing here is saved or reflects a real record.</p>
      {/* Mirrors the production planner header so it can be reviewed without an account. */}
      <div className="mb-5 flex flex-col gap-3 border-b border-[var(--ss-border)] pb-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <h1 className="truncate px-1 text-lg font-bold text-[var(--ss-text)] md:text-xl">CS B.S. + Math minor plan (example)</h1>
          <span className="px-1 text-sm text-[var(--ss-text-muted)]">Graduating <b className="font-semibold text-[var(--ss-text)]">Spring 2029</b></span>
          <span className="hidden flex-1 md:block" aria-hidden="true" />
          <span className="flex items-center gap-2">
            {autoFillNote ? <span className="text-xs text-[var(--ss-text-muted)]" role="status">{autoFillNote}</span> : null}
            <button type="button" onClick={runAutoFill} disabled={!catalog.length}
              className="flex min-h-12 md:min-h-10 items-center gap-2 rounded-control border border-[color-mix(in_srgb,var(--ss-brand)_45%,var(--ss-border))] px-3.5 text-sm font-semibold text-[var(--ss-brand)] hover:bg-[var(--ss-brand-bg)] disabled:opacity-50">
              <SparklesIcon className="h-4 w-4" aria-hidden="true" />Auto-fill the rest
            </button>
          </span>
        </div>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-6">
          <DegreeTargets targets={REQUIREMENTS.map((program) => ({ ID: program.degreeId, Name: program.degreeName }))} colorFor={(_degree, index) => programColor(index)} onView={() => undefined} onRemove={() => undefined} onAdd={() => undefined} />
          <div className="flex min-w-0 shrink-0 flex-col gap-1.5 lg:w-[26rem]">
            <p className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm text-[var(--ss-text-muted)]">
              <span><b className="tabular-nums text-[var(--ss-text)]">46</b> earned · <b className="tabular-nums text-[var(--ss-text)]">{plannedCredits}</b> planned · <b className="tabular-nums text-[var(--ss-text)]">{Math.max(0, 120 - 46 - plannedCredits)}</b> to place</span>
              <span className="whitespace-nowrap text-xs">of 120 cr</span>
            </p>
            <div className="flex h-1.5 overflow-hidden rounded-full bg-[var(--ss-surface-subtle)] ring-1 ring-inset ring-[var(--ss-border)]" role="img" aria-label={`46 earned credits, ${plannedCredits} planned credits, 120 target credits`}>
              <div className="h-full bg-[var(--ss-positive)]" style={{ width: `${(46 / 120) * 100}%` }} />
              <div className="planner-slot-planned h-full" style={{ width: `${Math.min(100 - (46 / 120) * 100, (plannedCredits / 120) * 100)}%` }} />
            </div>
          </div>
        </div>
      </div>
      <PlannerWorkspace
        openTransferOptions={transfer}
        semesters={semesters}
        selectedDegreeRequirements={REQUIREMENTS}
        degreeProgress={null}
        expandedPools={expandedPools}
        degreeCourseListsDirect={LISTS}
        userPrereqInfo={COMPLETED}
        creditsMap={creditsMap}
        earnedCredits={46}
        issues={issues}
        getCourseName={(code) => code}
        onAddCourseByCodeToSemester={(semesterNumber, code, _name, credits) => update((draft) => {
          draft.forEach((s) => { s.courses = s.courses.filter((c) => c.kind !== 'course' || normalize(c.code) !== normalize(code)); });
          draft.find((s) => s.number === semesterNumber)?.courses.push(course(code, semesterNumber, credits || 3));
        })}
        onAddCourseListByNameToSemester={(semesterNumber, mappingName, label, extra) => update((draft) => {
          draft.find((s) => s.number === semesterNumber)?.courses.push({ kind: 'course_list', mappingName, courseListId: extra?.courseListId ?? 0, label, credits: extra?.credits, semester: semesterNumber, completed: false });
        })}
        onDropCourse={(to, from, itemIndex) => update((draft) => {
          const source = draft.find((s) => s.number === from);
          const target = draft.find((s) => s.number === to);
          if (!source || !target) return;
          const [item] = source.courses.splice(itemIndex, 1);
          if (item) target.courses.push({ ...item, semester: to });
        })}
        onRemoveSemesterItem={(semesterNumber, itemIndex) => update((draft) => { draft.find((s) => s.number === semesterNumber)?.courses.splice(itemIndex, 1); })}
        onCourseListClick={() => undefined}
        onAddFromSearch={() => undefined}
        onAddSemester={() => update((draft) => {
          const last = draft[draft.length - 1];
          const [season, year] = (last?.name || 'Spring 2026').split(' ');
          const name = season === 'Fall' ? `Spring ${Number(year) + 1}` : `Fall ${year}`;
          draft.push({ number: (last?.number || 0) + 1, name, courses: [], totalCredits: 0 });
        })}
        onDeleteSemester={(semesterNumber) => update((draft) => { const i = draft.findIndex((s) => s.number === semesterNumber); if (i >= 0) draft.splice(i, 1); })}
        onDegreeImport={() => undefined}
        onAskCourseAI={() => undefined}
        proposedTerms={proposedTerms}
        onAcceptProposal={(proposal, accept) => {
          setSemesters((current) => applyProposal(current, proposal, accept));
          if (accept === 'all') setProposedTerms(null);
        }}
        onDiscardProposal={discardProposal}
        userCourseLists={USER_LISTS}
        userCourseListsReady
      />
    </div>
  );
}
